---
title: "Managing Leverage Like a Pro: Isolated vs Cross Margin"
slug: isolated-vs-cross-margin
shortTitle: "Isolated vs Cross Margin"
description: "How isolated and cross margin differ, why {{BRAND}} uses account-wide cross margin, and how to control leverage with position size, free margin and a stop on every trade."
level: advanced
category: trading
course: advanced
order: 8
topics: [leverage-margin, risk-management]
publishedAt: 2026-10-19
updatedAt: 2026-10-19
featured: false
popular: false
cover: /academy/img/covers/isolated-vs-cross-margin.svg
faqs:
  - q: "Does {{BRAND}} offer isolated margin?"
    a: "No. Every trading account uses cross margin, so your whole account equity backs all of your open positions. You control the risk of each trade with its position size and stop loss."
  - q: "What is the main danger of cross margin?"
    a: "A single losing position can draw on the equity that backs all your other trades. If losses push the margin level down to the stop-out level, positions are closed automatically, starting with the one losing the most."
  - q: "Is isolated margin safer?"
    a: "It caps the loss on one position at the margin allocated to it, barring gaps, but that position is liquidated after a much smaller move. Neither model is safe on its own; the size of your positions and your stops decide how much you can lose."
  - q: "Does choosing lower leverage reduce my risk on {{BRAND}}?"
    a: "Lower leverage reserves more margin per position, which leaves less free margin for new trades and acts as a brake on overall exposure. Your profit or loss per price move still depends only on position size, so size and stop loss matter most."
  - q: "How do I find my stop-out price?"
    a: "The platform shows your margin level rather than a liquidation price. For a single position, you can estimate it: work out how far equity can fall before it equals the stop-out percentage of your used margin, then divide that amount by your position size."
---

Every leveraged platform has to answer one question: when a position loses money, which money pays for it? With **isolated margin**, each position has its own ring-fenced pot, and only that pot is at risk. With **cross margin**, all of your account's equity stands behind every position, so a losing trade can draw on the whole balance. The choice changes how far a position can move against you, how much one trade can cost, and how the rest of your account is affected.

{{BRAND}} uses account-wide cross margin. This lesson explains both models, shows the difference with worked numbers, and then focuses on what matters in practice: managing leverage under cross margin with position size, free margin and a stop on every trade.

<Warning title="Leverage magnifies losses">

Margin trading lets you hold positions much larger than your deposit. Losses are calculated on the full position, can exceed the margin set aside for it, and can arrive quickly. Most traders who trade leveraged CFDs lose money. Only trade with money you can afford to lose.

</Warning>

<CTA />

<KeyTakeaways>

- **Isolated margin ring-fences each position.** A position can only lose its own margin, but it is closed after a much smaller move.
- **Cross margin pools everything.** Your whole equity backs every trade, giving positions more room while letting one loser drain the account.
- **{{BRAND}} is cross margin only.** There is no isolated mode, so your stops and position sizes are what limit each trade's loss.
- **Effective leverage is the number to watch.** Total position value divided by equity says more about your risk than the leverage setting on any single order.

</KeyTakeaways>

## A quick margin refresher

**Margin** is the money set aside to open a position: position value ÷ leverage. **Equity** is your balance plus credit plus the open profit or loss on your positions, swap included. **Free margin** is what is left over for new trades or withdrawals. **Margin level** is equity ÷ margin used, shown as a percentage. If the margin level falls far enough, the platform starts closing positions. [Leverage and margin explained simply](/academy/article/leverage-and-margin) covers these from the ground up.

## Isolated margin: one pot per position

Under isolated margin, which many crypto exchanges offer, you assign a fixed amount of collateral to each position. If the trade goes wrong, it is liquidated once losses approach that amount, and the rest of your balance is untouched. The upside is a clear cap on what one position can cost, barring gaps. The downside is that the position has little room: it gets closed after a smaller move, even if you had plenty of spare money in the account.

## Cross margin: one pot for everything

Under cross margin, every position draws on the same equity. A losing trade can keep running for as long as the account as a whole can carry it. That gives positions more breathing room, but it also means one bad trade can eat into the money backing all your other trades, and a stop-out can close positions you were happy with.

<Example title="The same BTC/USDT trade under both models (illustrative prices)">

Your account holds 2,000 USDT. You buy 0.1 BTC at 60,000, a position value of 0.1 × 60,000 = 6,000 USDT. At 20x leverage the margin is 6,000 ÷ 20 = **300 USDT**.

- **Isolated** (on a platform that offers it): only the 300 USDT is at risk. Ignoring fees and maintenance buffers, it is used up after a 300 ÷ 0.1 = 3,000 USDT drop, around **57,000**. The most you lose is roughly 300 USDT.
- **Cross**, with a hypothetical stop-out level of 50%: stop-out comes when equity falls to 50% × 300 = 150 USDT, that is, after a loss of 2,000 − 150 = 1,850 USDT. That needs a drop of 1,850 ÷ 0.1 = 18,500, to about **41,500**. The position survives a much larger move, but you can lose about six times as much.

</Example>

<CompareTable caption="Isolated and cross margin compared">

| | Isolated margin | Cross margin |
| --- | --- | --- |
| Collateral | A fixed amount per position | All account equity |
| Loss on one position | Capped near its margin, barring gaps | Can reach most of the account |
| Room before closing | Small | Large |
| Effect on other trades | None | A loser reduces free margin for all |
| What limits risk | The margin you assign | Your position size and stop loss |
| On {{BRAND}} | Not offered | How every account works |

</CompareTable>

## How margin works on {{BRAND}}

Every {{BRAND}} account uses cross margin: your whole equity backs every open position, and pending orders reserve margin as soon as they are placed. You choose leverage per order with the **Leverage: Nx** button, which opens the **Adjust Leverage** slider, up to {{MAX_LEVERAGE}}.

The client bar shows **Equity**, **Open P/L**, **Balance**, **Free Margin**, **Used Margin** and **Margin Level**. You receive alerts when the margin level reaches {{MARGIN_WARNING_LEVEL}} and then {{MARGIN_CRITICAL_LEVEL}}. At {{STOP_OUT_LEVEL}}, positions are closed one at a time, starting with the one showing the largest loss, until the margin level recovers. The platform does not display a liquidation price, but you can estimate one; see [margin, leverage and liquidation price on {{BRAND}}](/academy/article/margin-leverage-liquidation-price) for the full method.

<Screenshot name="client-bar-margin-level" alt="The client bar showing Equity, Open P/L, Balance, Free Margin, Used Margin and Margin Level" caption="The client bar: the numbers that tell you how much room your account has." />

{/* TODO: verify with platform — cross margin only (no isolated mode), alert and stop-out behavior, and a real client-bar screenshot as client-bar-margin-level.png. */}

### What the leverage slider does, and does not do

Under cross margin, the leverage you pick decides how much margin a position reserves. It does **not** change how much you gain or lose per dollar of price movement: that depends only on position size. Take the example above at 5x instead of 20x. The margin is 6,000 ÷ 5 = 1,200 USDT, so the margin level starts lower and warnings and stop-out arrive sooner: with the same hypothetical 50% stop-out level, after a loss of 2,000 − 600 = 1,400 USDT instead of 1,850. Higher leverage does the opposite: it frees up margin, which makes it easy to keep opening positions until your total exposure is far larger than you meant.

That is why experienced traders watch **effective leverage**: the total value of all open positions ÷ equity. In the example it is 6,000 ÷ 2,000 = **3x** whichever slider setting you use.

## Managing leverage under cross margin

<Steps>
<Step title="Size every trade from its stop">

Decide the USDT you are willing to lose, switch on **TP / SL**, and adjust the size until the **Loss (USDT)** figure at your stop matches it. The stop is your own isolation.

</Step>
<Step title="Keep a ceiling on effective leverage">

Add up the **Value** column in **Open Positions** and divide by your equity. Set your own ceiling, for example 3x, and do not open new trades above it.

</Step>
<Step title="Leave a free-margin buffer">

Check **Free margin after** in the order summary before you confirm. Using less than the maximum leverage, and fewer positions at once, keeps your margin level well above {{MARGIN_WARNING_LEVEL}}.

</Step>
<Step title="Count related trades together">

Positions that move together pull the margin level down together. [Hedging and correlation](/academy/article/hedging-and-correlation) shows how to check this.

</Step>
<Step title="Set your own early warning">

In **Account → Alerts**, add a **Margin level** alert at a level comfortably above the platform's warning, so you have time to act.

</Step>
</Steps>

<ProTip title="Build the isolation yourself">

Cross margin will not stop one trade from draining the account, but a stop loss on every position does most of that job. Treat a position without a stop as one that can, in a bad enough move, reach your whole balance.

</ProTip>

Remember that a stop loss fills at market. In a gap or a very fast move, it can close beyond your level, so leave extra room between your worst planned loss and the stop-out level.

## Conclusion

Isolated margin caps each position's loss at its own pot but closes trades after small moves; cross margin gives positions room by putting your whole equity behind them, and lets one loser affect everything. {{BRAND}} uses cross margin, so the controls that matter are yours: position size, a stop on every trade, a ceiling on effective leverage, a healthy free-margin buffer and an early margin alert. Manage those, and the leverage slider becomes a detail rather than a risk.
